import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { normalizeUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Member } from "@/data/members";
import { Mail, Phone, Globe, Linkedin, Facebook, Instagram, User, UserPlus } from "lucide-react";
import SnapchatIcon from "@/components/icons/SnapchatIcon";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface MemberCardProps {
  member: Member;
}

const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const generateVCard = (member: Member) => {
  const nameParts = member.name.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';
  
  let vcard = 'BEGIN:VCARD\n';
  vcard += 'VERSION:3.0\n';
  vcard += `FN:${member.name}\n`;
  vcard += `N:${lastName};${firstName};;;\n`;
  vcard += `ORG:${member.company}\n`;
  vcard += `TITLE:${member.title}\n`;
  
  if (member.phone) {
    vcard += `TEL;TYPE=WORK,VOICE:${member.phone}\n`;
  }
  
  if (member.email) {
    vcard += `EMAIL;TYPE=INTERNET:${member.email}\n`;
  }
  
  if (member.website) {
    vcard += `URL:${normalizeUrl(member.website)}\n`;
  }
  
  if (member.description) {
    vcard += `NOTE:${member.description}\n`;
  }
  
  if (member.image) {
    vcard += `PHOTO;VALUE=URL:${member.image}\n`;
  }
  
  vcard += 'END:VCARD';
  
  return vcard;
};

const downloadVCard = (member: Member) => {
  const vcard = generateVCard(member);
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${member.name.replace(/\s+/g, '_')}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const copyContactToClipboard = async (member: Member) => {
  const parts = [
    `${member.name}`,
    `${member.company}`,
    `${member.title}`,
    '',
  ];
  
  if (member.email) parts.push(`Email: ${member.email}`);
  if (member.phone) parts.push(`Phone: ${member.phone}`);
  if (member.website) parts.push(`Website: ${member.website}`);
  
  if (member.description) {
    parts.push('');
    parts.push(`Services: ${member.description}`);
  }
  
  if (member.about) {
    parts.push('');
    parts.push(`About: ${member.about}`);
  }
  
  if (member.linkedin || member.facebook || member.instagram || member.snapchat) {
    parts.push('');
    if (member.linkedin) parts.push(`LinkedIn: ${member.linkedin}`);
    if (member.facebook) parts.push(`Facebook: ${member.facebook}`);
    if (member.instagram) parts.push(`Instagram: ${member.instagram}`);
    if (member.snapchat) parts.push(`Snapchat: ${member.snapchat}`);
  }
  
  const contactText = parts.filter(p => p !== undefined).join('\n');
  await navigator.clipboard.writeText(contactText);
};

const createOutlookContactsLink = (member: Member) => {
  const params = new URLSearchParams();
  const nameParts = member.name.split(' ');
  params.append('given_name', nameParts[0] || '');
  params.append('family_name', nameParts.slice(1).join(' ') || '');
  params.append('company', member.company);
  params.append('job_title', member.title);
  if (member.email) params.append('email', member.email);
  if (member.phone) params.append('phone', member.phone);
  if (member.website) params.append('website', member.website);
  if (member.description) params.append('notes', member.description);
  
  return `https://outlook.office.com/people/action/new?${params.toString()}`;
};

const createGoogleContactsLink = (member: Member) => {
  const params = new URLSearchParams();
  const nameParts = member.name.split(' ');
  params.append('given_name', nameParts[0] || '');
  params.append('family_name', nameParts.slice(1).join(' ') || '');
  params.append('company', member.company);
  params.append('job_title', member.title);
  if (member.email) params.append('email', member.email);
  if (member.phone) params.append('phone', member.phone);
  if (member.website) params.append('website', member.website);
  if (member.description) params.append('notes', member.description);
  
  return `https://contacts.google.com/new?${params.toString()}`;
};

const MemberCard = ({ member }: MemberCardProps) => {
  const { toast } = useToast();

  const handleAddContact = async () => {
    if (isMobile()) {
      downloadVCard(member);
    } else {
      await copyContactToClipboard(member);
      toast({
        title: "Contact copied!",
        description: "Contact details have been copied to your clipboard.",
      });
    }
  };

  return (
    <Card className="h-full flex flex-col hover:shadow-xl transition-all duration-300 group">
      <CardHeader className="pb-4">
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-full bg-muted flex-shrink-0 overflow-hidden border-2 border-primary/20 group-hover:border-primary/40 transition-colors">
            {member.image ? (
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary/10">
                <User className="w-10 h-10 text-primary/40" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-xl mb-1">{member.name}</CardTitle>
            <p className="text-sm font-medium text-primary">{member.company}</p>
            <p className="text-sm text-muted-foreground">{member.title}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <CardDescription className="flex-1 mb-4 line-clamp-3">
          {member.description}
        </CardDescription>

        {/* Quick Contact Links */}
        <div className="flex flex-wrap gap-2 mb-4">
          {member.website && (
            <a
              href={normalizeUrl(member.website) ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <Globe className="w-3 h-3" />
              Website
            </a>
          )}
          {member.email && (
            <a
              href={`mailto:${member.email}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <Mail className="w-3 h-3" />
              Email
            </a>
          )}
          {member.phone && (
            <a
              href={`tel:${member.phone}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <Phone className="w-3 h-3" />
              Call
            </a>
          )}
        </div>

        {/* View Details Button */}
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full">
              View Full Profile
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-24 h-24 rounded-full bg-muted flex-shrink-0 overflow-hidden border-2 border-primary/20">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/10">
                      <User className="w-12 h-12 text-primary/40" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-2xl mb-1">{member.name}</DialogTitle>
                  <p className="text-base font-medium text-primary">{member.company}</p>
                  <p className="text-sm text-muted-foreground">{member.title}</p>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-6">
              {/* Add Contact Button */}
              <div className="space-y-3">
                <Button 
                  onClick={handleAddContact}
                  className="w-full gap-2"
                  variant="default"
                >
                  <UserPlus className="w-4 h-4" />
                  {isMobile() ? 'Download Contact Card' : 'Copy Contact Details'}
                </Button>
                
                {/* Desktop-only options */}
                {!isMobile() && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="text-xs"
                    >
                      <a 
                        href={createOutlookContactsLink(member)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Outlook Contacts
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="text-xs"
                    >
                      <a 
                        href={createGoogleContactsLink(member)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Google Contacts
                      </a>
                    </Button>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h4 className="font-semibold mb-2">Services</h4>
                <p className="text-muted-foreground">{member.description}</p>
              </div>

              {/* About */}
              {member.about && (
                <div>
                  <h4 className="font-semibold mb-2">About</h4>
                  <p className="text-muted-foreground">{member.about}</p>
                </div>
              )}

              {/* Contact Information */}
              <div>
                <h4 className="font-semibold mb-3">Contact Information</h4>
                <div className="space-y-2">
                  {member.website && (
                    <a
                      href={normalizeUrl(member.website) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      <span className="break-all">{member.website}</span>
                    </a>
                  )}
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      <span>{member.email}</span>
                    </a>
                  )}
                  {member.phone && (
                    <a
                      href={`tel:${member.phone}`}
                      className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{member.phone}</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Social Links */}
              {(member.linkedin || member.facebook || member.instagram || member.snapchat) && (
                <div>
                  <h4 className="font-semibold mb-3">Social Media</h4>
                  <div className="flex flex-wrap gap-3">
                    {member.linkedin && (
                      <a
                        href={normalizeUrl(member.linkedin) ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn
                      </a>
                    )}
                    {member.facebook && (
                      <a
                        href={normalizeUrl(member.facebook) ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </a>
                    )}
                    {member.instagram && (
                      <a
                        href={member.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        <Instagram className="w-4 h-4" />
                        Instagram
                      </a>
                    )}
                    {member.snapchat && (
                      <a
                        href={member.snapchat}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        <SnapchatIcon className="w-4 h-4" />
                        Snapchat
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MemberCard;
